<template>
    <div class="container">
        <TreeMenu class="radioSettingsTree" :treeData="MENU_TREE" @node-click="handleNodeClick" />
        <div class="content">
            <div class="setting-label">
                {{ settingFirstLabel }} &gt; {{ settingSecondLabel }}
            </div>

            <template v-if="getDisplayItems().length > 0">
                <div v-for="itemObj in getDisplayItems()" :key="itemObj.name || itemObj.label">
                    <component
                        v-if="useUniqueComp()"
                        :is="currentTabComponent"
                        :first="selectedFirstLevel"
                        :second="selectedSecondLevel"
                        :setting="setting"
                        :key="setting"
                    />
                    <el-table v-else :data="getTableRows(itemObj)">
                        <el-table-column prop="label" :label="itemObj.label" />
                        <el-table-column prop="value" label="設定値" />
                    </el-table>
                </div>
            </template>
            <template v-else>
                <!-- 全設定項目対応完了時に削除 -->
                <div class="not-supported">未対応</div>
            </template>
        </div>
    </div>
</template>

<script>
import humps from 'humps'
import { componentsMap } from './BCCurrentSettingsComponentsMap.js'
import TreeMenu from './atoms/TreeMenu.vue'
import { MENU_TREE } from './config/BCconstants'
import {
    FIRST_LEVEL_MENU,
    SECOND_LEVEL_MENU,
    THIRD_LEVEL_MENU,
    BC_TRANS_JP,
    SPECIAL_CASE_VIEW,
    FUNCTION_LIST,
} from './config/BCconstants.js'

export default {
    name: 'BCCurrentSettingsVue2',
    components: { TreeMenu },
    props: {
        setting: {
            type: Object,
            default: () => ({}),
        },
    },
    data () {
        const first = (FIRST_LEVEL_MENU[0] && FIRST_LEVEL_MENU[0].name) || ''
        const secondList = (SECOND_LEVEL_MENU[first]) || []
        const second = (secondList[0] && secondList[0].name) || ''
        return {
            // state
            selectedFirstLevel: first,
            selectedSecondLevel: second,
            // constants for template access if needed
            MENU_TREE,
            FIRST_LEVEL_MENU,
            SECOND_LEVEL_MENU,
            THIRD_LEVEL_MENU,
            BC_TRANS_JP,
            SPECIAL_CASE_VIEW,
            FUNCTION_LIST,
            componentsMap,
        }
    },
    computed: {
        settingFirstLabel () {
            const found = FIRST_LEVEL_MENU.find(i => i.name === this.selectedFirstLevel)
            return (found && found.label) || ''
        },
        settingSecondLabel () {
            const list = SECOND_LEVEL_MENU[this.selectedFirstLevel] || []
            const found = list.find(i => i.name === this.selectedSecondLevel)
            return (found && found.label) || ''
        },
        currentTabComponent () {
            const key =
                humps.pascalize(this.selectedFirstLevel) +
                humps.pascalize(this.selectedSecondLevel)
            return this.componentsMap[key] || null
        },
    },
    methods: {
        handleNodeClick (data) {
            // ルートノードや不正なデータは無視
            if (!data || !data.node || !data.node.isLeaf) return
            this.selectedFirstLevel = (data.parents && data.parents[0] && data.parents[0].name) || ''
            this.selectedSecondLevel = (data.node && data.node.name) || ''
            this.$emit('select', { first: this.selectedFirstLevel, second: this.selectedSecondLevel })
        },

        // Displayの設定項目リスト取得
        getDisplayItems () {
            const items =
                (THIRD_LEVEL_MENU[this.selectedFirstLevel] &&
                    THIRD_LEVEL_MENU[this.selectedFirstLevel][this.selectedSecondLevel]) ||
                []
            const baseData =
                this.setting &&
                this.setting[this.selectedFirstLevel] &&
                this.setting[this.selectedFirstLevel][this.selectedSecondLevel]
            return items.filter(item => this.isItemVisible(item, baseData))
        },

        // 現在の設定値を取得（ネスト対応）
        getSettingValue (name, parentName = null) {
            const data =
                this.setting &&
                this.setting[this.selectedFirstLevel] &&
                this.setting[this.selectedFirstLevel][this.selectedSecondLevel]
            if (!data) return ''

            // キー割当てのファンクション項目対応
            if (
                name === 'Function' &&
                (parentName === 'Up' ||
                    parentName === 'Down' ||
                    parentName === 'Enter' ||
                    parentName === 'Back' ||
                    parentName === 'Side1' ||
                    parentName === 'Side2' ||
                    parentName === 'REM' ||
                    parentName === 'Button2' ||
                    parentName === 'Button3' ||
                    parentName === 'Button4' ||
                    parentName === 'Button5')
            ) {
                const parent = data[parentName] || {}
                const funcPress = parent['Function(Press)']
                const funcRelease = parent['Function(Release)']
                return funcPress !== 'None' ? funcPress : (funcRelease !== 'None' ? funcRelease : 'None')
            }

            // ネスト項目の場合
            if (parentName && data[parentName]) {
                return data[parentName][name] != null ? data[parentName][name] : ''
            }
            return data[name] != null ? data[name] : ''
        },

        // テーブル用データ生成
        getTableRows (itemObj) {
            const baseData =
                this.setting &&
                this.setting[this.selectedFirstLevel] &&
                this.setting[this.selectedFirstLevel][this.selectedSecondLevel]
            const parentData = itemObj.name ? (baseData && baseData[itemObj.name]) : baseData
            const currentKeys = [itemObj.name].filter(Boolean)

            const list = (itemObj.item || []).filter(item => this.isItemVisible(item, parentData))
            return list.map(item => {
                let value = this.getSettingValue(item.name, itemObj.name || null)
                value = this.formatValue(value, item, currentKeys)
                return { label: item.label, value }
            })
        },

        // 項目の表示可否判定
        // TODO: visibleがfunctionの場合の対応
        // TODO: テーブル自体の表示切替のために判定が混同しているため整理
        isItemVisible (item, baseData) {
            if (baseData === undefined) return false
            const visible = item.visible
            if (!visible || typeof visible !== 'object') return true

            // テーブル自体の表示・非表示判定
            if (Array.isArray(visible)) {
                return visible.some(v => {
                    const parent = baseData[v.parentName]
                    return parent && parent[v.name] === v.value
                })
            }

            if (visible.parentName) {
                const parent = baseData[visible.parentName]
                return parent && parent[visible.name] === visible.value
            }

            const data = visible.name ? baseData[visible.name] : undefined
            return data === visible.value
        },

        toUpperSnake (str) {
            if (typeof str !== 'string') return ''
            return str
                .replace(/\+/g, '')            // "+"を削除
                .replace(/&/g, 'And')          // "&"を変換
                .replace(/([A-Z]+)([A-Z][a-z]{2,})/g, '$1_$2') // 略語_単語(2文字以上)
                .replace(/([a-z0-9])([A-Z])/g, '$1_$2') // 小文字_大文字
                .replace(/[-\s]/g, '_')        // ハイフン・スペース→_
                .replace(/_+/g, '_')           // 連続アンダースコアを1つに
                .toUpperCase()
        },

        // 再帰的にオブジェクト内を探索し、searchValueに一致する値を返す
        findCommonValueRecursive (obj, searchValue) {
            if (!obj || typeof obj !== 'object') return undefined
            for (const key of Object.keys(obj)) {
                const val = obj[key]
                if (key === searchValue) return val
                if (val && typeof val === 'object') {
                    const found = this.findCommonValueRecursive(val, searchValue)
                    if (found !== undefined) return found
                }
            }
            return undefined
        },

        // 多階層の翻訳オブジェクト探索
        getTransObjByKeys (keys, transObj) {
            let obj = transObj
            for (const key of keys) {
                if (!obj) return undefined
                obj = obj[this.toUpperSnake(key)]
            }
            return obj
        },

        formatValue (value, item, parentKeys = []) {
            let jpValue = value

            const itemLabelKey = (item && item.name) ? this.toUpperSnake(item.name) : ''
            let searchValue = value ? this.toUpperSnake(value) : ''
            const keys = [this.selectedFirstLevel, this.selectedSecondLevel, ...parentKeys, itemLabelKey]
                .filter(Boolean)
                .map(this.toUpperSnake)

            const searchObj = this.getTransObjByKeys(keys, BC_TRANS_JP)
            const searchCommonObj = (BC_TRANS_JP && BC_TRANS_JP.COMMON) ? BC_TRANS_JP.COMMON : {}

            // ファンクションリストの選択肢の場合（※ Function系項目に限定して適用する）
            const isFunctionItem =
                item &&
                (item.name === 'Function' ||
                    item.name === 'HoldFunction' ||
                    item.name === 'Function(Press)' ||
                    item.name === 'Function(Release)')

            if (isFunctionItem) {
                const foundFunc = FUNCTION_LIST.find(ele => ele.value === value)
                if (foundFunc) {
                    return foundFunc.label
                }
            }

            if (item && item.specialCase) {
                const specialCaseObj = this.getTransObjByKeys(keys, SPECIAL_CASE_VIEW) || {}

                if (specialCaseObj.judge === 'notNull') {
                    searchValue = specialCaseObj.value
                    jpValue = specialCaseObj.value
                } else if (String(value) === String(specialCaseObj.judge)) {
                    // placeが指定されている場合は、その項目の値を取得して表示
                    if (specialCaseObj.place) {
                        const parentName = parentKeys[parentKeys.length - 1] || ''
                        const displayItems = this.getDisplayItems()
                        const parentItemObj = displayItems.find(i => i.name === parentName)
                        const placeItemObj =
                            (parentItemObj && parentItemObj.item)
                                ? (parentItemObj.item.find(i => i.name === specialCaseObj.place) || { name: specialCaseObj.place })
                                : { name: specialCaseObj.place }

                        const placeValue = this.getSettingValue(
                            specialCaseObj.place,
                            parentKeys[parentKeys.length - 1] || null
                        )
                        const placeJpValue = this.formatValue(placeValue, placeItemObj, parentKeys)
                        searchValue = placeJpValue
                        jpValue = placeJpValue
                    } else {
                        searchValue = specialCaseObj.value
                        jpValue = specialCaseObj.value
                    }
                }
            }

            if (searchObj && searchValue) {
                if (Object.prototype.hasOwnProperty.call(searchObj, searchValue)) {
                    jpValue = searchObj[searchValue]
                } else {
                    const foundCommon = this.findCommonValueRecursive(searchCommonObj, searchValue)
                    if (foundCommon !== undefined) jpValue = foundCommon
                }
            } else if (searchCommonObj && searchValue) {
                const foundCommon = this.findCommonValueRecursive(searchCommonObj, searchValue)
                if (foundCommon !== undefined) jpValue = foundCommon
            }

            if (item && item.unit) {
                jpValue = `${jpValue} ${item.unit}`
            }
            return jpValue
        },

        useUniqueComp () {
            const S = SECOND_LEVEL_MENU
            const sel = this.selectedSecondLevel
            return (
                sel === (S.KeyAssignment[4] && S.KeyAssignment[4].name) ||
                sel === (S.KeyAssignment[5] && S.KeyAssignment[5].name) ||
                sel === (S.KeyAssignment[6] && S.KeyAssignment[6].name) ||
                sel === (S.OptionalFeatures[9] && S.OptionalFeatures[9].name) ||
                sel === (S.Network[0] && S.Network[0].name) ||
                sel === (S.Network[3] && S.Network[3].name) ||
                sel === (S.Network[4] && S.Network[4].name) ||
                sel === (S.SelCall[1] && S.SelCall[1].name) ||
                sel === (S.SelCall[2] && S.SelCall[2].name)
            )
        },
    },
}
</script>

<style scoped>
.container {
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: 1fr 2fr; /* 各幅は暫定の値 */
  height: 100%;
}

.content {
    height: calc(100vh - 64px - 49px);
    overflow-y: auto;
}

.radioSettingsTree {
  height: calc(100vh - 64px - 49px); /* ヘッダーとサブヘッダーの高さを引いた値 */
}

:deep(.el-tree-node__content) {
  height: 40px; /* Buddycom Consoleより参照 */
}

.setting-label {
  padding: 12px 16px;
  margin-bottom: 0;
  font-weight: 500;
  color: #888;
  background-color: #f5f5f5;
}

/* 全設定項目対応完了時に削除 */
.not-supported {
  padding: 24px;
  font-size: 1.2em;
  color: #888;
  text-align: center;
}

::v-deep(.el-table th) {
  color: #888 !important; /* グレー文字 */
  background-color: #f5f5f5 !important; /* 薄いグレー */
}
</style>
